import { z } from "zod";
import { getIdSchema, ID, makeIdGenerator } from "../utils";
import { accountIdSchema } from "./accountSchema";

export const userIdPrefix = "usr_";
export type UserId = ID<typeof userIdPrefix>;
export const generateUserId = makeIdGenerator(userIdPrefix);
export const userIdSchema = getIdSchema(userIdPrefix, "UserId");

// Mirrors the Prisma `UserRole` enum (ADR-0004). `user` is every tenant
// user; `site_admin` adds operator powers. Keep in sync with schema.prisma.
export const userRoleSchema = z.enum(["user", "site_admin"]);
export type UserRole = z.infer<typeof userRoleSchema>;

export const userSchema = z
  .object({
    id: userIdSchema,
    accountId: accountIdSchema,
    name: z.string(),
    email: z.string(),
    username: z.string(),
    role: userRoleSchema,
    lastActiveAt: z.coerce.date().nullable(),
    previousActiveAt: z.coerce.date().nullable(),
  })
  .meta({ id: "User" });

export type User = z.infer<typeof userSchema>;

// /api/me response (ADR-0004 §6.3): the *effective* user, plus — only while a
// site admin is impersonating — the authenticated admin behind the session,
// so the frontend can render the TopBar impersonation indicator.
export const meSchema = userSchema
  .extend({
    actor: z
      .object({
        id: userIdSchema,
        name: z.string(),
      })
      .nullable(),
  })
  .meta({ id: "Me" });

export type Me = z.infer<typeof meSchema>;

// GET /api/debug/users (docs/client-telemetry.md): the operator's view of a
// user for picking whose telemetry to read or whom to impersonate. Adds the
// login/activity timestamps `userSchema` leaves out; never the password hash.
export const debugUserSchema = userSchema
  .extend({
    lastLogin: z.coerce.date().nullable(),
    accountIsDuplicate: z.boolean(),
  })
  .meta({ id: "DebugUser" });

export type DebugUser = z.infer<typeof debugUserSchema>;
